<template>
  <v-card :class="`dashboard ${viewport}-viewport`" flat :style="cssVars">
    <v-row no-gutters>
      <v-col cols="12" md="3">
        <div class="d-flex align-center">
          <v-card-title class="page-title" data-cy="dashboardHeader">
            {{ $t('dashboard.title') }}
          </v-card-title>
          <v-btn
            @click="openAddWidgets"
            variant="outlined"
            :disabled="!devicesCounts.totalDevices"
            class="text-black icon-btn card-title-btn"
          >
            <v-icon icon="mdi-view-dashboard" />
            <v-icon icon="mdi-plus" size="x-small" class="append-plus" />
          </v-btn>
        </div>
      </v-col>
      <v-col cols="12" md="6" align="center">
        <div class="devices-summary" data-cy="devicesSummary">
          <router-link to="/manage-devices">
            {{ $t('dashboard.devices') }}
            <span md="3" class="devices-summary__group border-right" data-cy="devicesSummaryTotal">
              <span class="devices-count">{{ devicesCounts.totalDevices }}</span>
              <span>{{ $t('dashboard.total') }}</span>
            </span>
            <span md="3" class="devices-summary__group border-right" data-cy="devicesSummaryActive">
              <span class="active devices-count">{{ devicesCounts.activeDevices }}</span>
              <span>{{ $t('dashboard.active') }}</span>
            </span>
            <span md="3" class="devices-summary__group" data-cy="devicesSummaryInactive">
              <span class="inactive devices-count">{{ devicesCounts.inactiveDevices }}</span>
              <span>{{ $t('dashboard.inactive') }}</span>
            </span>
          </router-link>
        </div>
      </v-col>
    </v-row>

    <GridLayout
      v-show="!grid.loading"
      ref="dashboardGrid"
      class="dashboard-grid"
      data-cy="dashboardGrid"
      v-model:layout="grid.layout"
      :col-num="colNum"
      :row-height="grid.rowHeight"
      :margin="margin"
      :is-draggable="grid.isDraggable"
      :is-resizable="grid.isResizable"
      :vertical-compact="verticalCompact"
      :restore-on-drag="restoreOnDrag"
      :use-css-transforms="false"
      :responsive="false"
      :breakpoints="grid.breakpoints"
      :cols="grid.cols"
      @layout-updated="layoutUpdated"
    >
      <GridItem
        v-for="item in grid.layout"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
        :key="item.i"
        :isResizable="item.isResizeable"
        :maxW="item.maxW"
        :maxH="item.maxH"
        drag-allow-from=".vue-draggable-handle"
        drag-ignore-from=".no-drag, .widget-fullscreen .vue-draggable-handle"
        @resized="setWidgetDims"
        @container-resized="setWidgetDims"
        @moved="setWidgetMoved"
      >
        <Teleport to="body" :disabled="!item.isFullscreen">
          <component
            v-if="widgetsById[item.i]"
            :widgetId="widgetsById[item.i].id"
            :widgetType="widgetsById[item.i].widgetComponent"
            :widgetMaxDevices="widgetMaxDevices"
            :deviceIds="widgetsById[item.i].deviceIds"
            :width="widgetsById[item.i].width"
            :height="widgetsById[item.i].height"
            :settings="widgetsById[item.i].settings"
            :is="resolveWidgetComponent(widgetsById[item.i].widgetComponent)"
            :class="{ disabled: widgetsById[item.i].disabled }"
            @fullscreenChange="toggleFullscreen(item.i, $event)"
            @devicesChanged="updateWidgetDevices(item.i, $event)"
            @removeWidget="handleRemoveWidget(item.i, $event)"
            @refreshDashboard="refreshDashboard()"
          />
        </Teleport>
        <LoadingContainer v-if="!devicesLength" />
      </GridItem>
    </GridLayout>

    <div v-show="grid.loading" class="text-center emptyMessage">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div class="text-center empty-message" data-cy="dashboardEmptyMessage" v-if="showEmptyMessage">
      {{ $t('dashboard.messages.empty') }}
    </div>
    <AddWidgetsDialog
      :open="dialogs.addWidgets"
      :widgets="widgets"
      :widgetMaxDevices="widgetMaxDevices"
      @close="closeAddWidgets"
      @widgetAdded="addNewWidget($event)"
      data-cy="addWidgetsDialog"
    />
    <RemoveWarningDialog
      :open="dialogs.removeWarning"
      @ok="removeWidget"
      @cancel="clearWidgetToRemove"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type Component } from 'vue'
import { FusionStores } from '@fusion/saas-components-v3'
import { GridLayout, GridItem } from 'grid-layout-plus'
import type { Breakpoints } from 'grid-layout-plus'
import throttle from 'lodash/throttle'

import LoadingContainer from '@/components/devicePopover/layouts/LoadingContainer.vue'
import PopoverWidget from '@/components/widgets/currentReadings/CurrentReadingsWidget.vue'
import MapWidget from '@/components/widgets/MapWidget.vue'
import HistoryWidget from '@/components/widgets/HistoryWidget.vue'
import AddWidgetsDialog from '@/components/dialogs/AddWidgetsDialog.vue'
import RemoveWarningDialog from '@/components/dialogs/RemoveWarningDialog.vue'
import { HTTPStatus } from '@/api'
import { activeLastDay } from '@/services/device-status'
import { MessageType, SNACKBAR_STATIC_DURATION_MS } from '@/services/notifications'
import { FeatureFlag } from '@/services/feature-flags'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import type { Layouts, Widget, LayoutWidgetProperties, PostWidgetRequest } from '@/types/dashboard'
import { NAMESPACES } from '@/store'
import { DEVICE_GETTERS } from '@/store/devices'
import type { UnpackedDevice } from '@/services/device-service/devices'

const MAX_DEVICES = 1
const MAX_DEVICES_HISTORY = 5

export type WidgetComponentKey = 'MapWidget' | 'PopoverWidget' | 'HistoryWidget'

const WidgetComponents: Record<WidgetComponentKey, Component> = {
  MapWidget: MapWidget,
  PopoverWidget: PopoverWidget,
  HistoryWidget: HistoryWidget
} as const

type WidgetSize = 'sm' | 'md' | 'lg'

export type Viewport = 'xxs' | 'xs' | 'sm' | 'md' | 'lg'

interface LayoutItem extends LayoutWidgetProperties {
  x: number
  y: number
  w: number
  h: number
  i: string
  isFullscreen?: boolean
  userModified?: boolean
}

export interface DashboardWidget extends Widget {
  width: number
  height: number
  disabled?: boolean
  isResizable?: boolean
  w?: number
  h?: number
  maxW?: number
  maxH?: number
}

export type GridLayouts = Record<Viewport, LayoutItem[]>
export type GridLayoutsLocked = Record<Viewport, boolean>

interface GridDims {
  w: number
  h: number
}

type GridSizes = Record<WidgetSize, { w: number; h: number }>

type GridMargins = Record<Viewport, [number, number]>

interface GridState {
  dims: GridDims
  layout: LayoutItem[]
  originalLayout: LayoutItem[]
  layouts: GridLayouts
  layoutsLocked: GridLayoutsLocked
  widgetMoved: boolean
  loading: boolean
  rowHeight: number
  margins: GridMargins
  isDraggable: boolean
  isResizable: boolean
  breakpoints: Breakpoints
  cols: Breakpoints
  sizes: GridSizes
}

interface GridMap {
  width: number
  height: number
  map: string[][]
}

export default defineComponent({
  name: 'Dashboard',
  components: {
    GridLayout,
    GridItem,
    AddWidgetsDialog,
    RemoveWarningDialog,
    LoadingContainer,
    PopoverWidget,
    MapWidget,
    HistoryWidget
  },
  setup() {
    const featureFlagsStore = FusionStores.featureFlags()
    const notifications = FusionStores.notifications()
    return { featureFlagsStore, notifications }
  },
  data() {
    return {
      accountId: null as string | null,
      grid: {
        dims: {
          w: -1,
          h: -1
        },
        layout: [],
        originalLayout: [],
        layouts: {},
        layoutsLocked: {},
        widgetMoved: false,
        loading: true,
        rowHeight: 450,
        margins: {
          lg: [40, 40],
          md: [40, 40],
          sm: [24, 24],
          xs: [24, 24],
          xxs: [16, 16]
        },
        isDraggable: true,
        isResizable: true,
        breakpoints: { lg: 2000, md: 1600, sm: 1200, xs: 700, xxs: 0 },
        cols: { lg: 5, md: 4, sm: 3, xs: 2, xxs: 1 },
        sizes: {
          sm: { w: 1, h: 1 },
          md: { w: 2, h: 1 },
          lg: { w: 2, h: 2 }
        }
      } as GridState,
      widgets: [] as DashboardWidget[],
      widgetSizes: {
        MapWidget: ['sm', 'md', 'lg'],
        PopoverWidget: ['sm'],
        HistoryWidget: ['sm', 'md', 'lg']
      } as Record<WidgetComponentKey, WidgetSize[]>,
      widgetMaxDevices: {
        MapWidget: MAX_DEVICES,
        PopoverWidget: MAX_DEVICES,
        HistoryWidget: MAX_DEVICES_HISTORY
      } as Record<WidgetComponentKey, number>,
      widgetToRemove: null as string | null,
      dialogs: {
        addWidgets: false,
        removeWarning: false
      },
      isLoaded: false,
      observer: null as ResizeObserver | null
    }
  },
  computed: {
    /**
     * Compare grid element width to specified breakpoints
     * @returns String of current viewport
     */
    viewport(): Viewport | '' {
      if (this.isLoaded) {
        let viewport: Viewport
        if (this.grid.dims.w >= this.grid.breakpoints.xxs) viewport = 'xxs'
        if (this.grid.dims.w >= this.grid.breakpoints.xs) viewport = 'xs'
        if (this.grid.dims.w >= this.grid.breakpoints.sm) viewport = 'sm'
        if (this.grid.dims.w >= this.grid.breakpoints.md) viewport = 'md'
        if (this.grid.dims.w >= this.grid.breakpoints.lg) viewport = 'lg'
        return viewport ?? ''
      }
      return ''
    },
    colNum(): number {
      return this.viewport ? this.grid.cols[this.viewport] : 3
    },
    rowHeight(): number {
      return Math.round(this.grid.dims.w / this.colNum)
    },
    margin(): [number, number] {
      return this.viewport ? this.grid.margins[this.viewport] : [24, 24]
    },
    cssVars(): Record<string, string> {
      return {
        '--row-height': `${this.grid.rowHeight}px`,
        '--widget-margin': `${this.margin[0]}px`
      }
    },
    /**
     * Build a 2D grid map of the layout for each viewport
     * Marks each cell with widget ID or as empty
     * @return  {Object} with properties for each viewport
     *          Each viewport {Object} includes width {Number},
     *          height {Number}, and map {Array} properties
     */
    gridMaps(): Record<string, GridMap> {
      const grids: Record<string, GridMap> = {}
      for (const viewport in this.grid.layouts) {
        const layout = this.grid.layouts[viewport as Viewport]
        const width = (this.grid.cols as Record<string, number>)[viewport]
        const height = layout.length > 0 ? Math.max(...layout.map((item) => item.y + item.h)) : 1
        const map = this.buildGridMap(layout, width, height)
        grids[viewport] = { width, height, map }
      }
      return grids
    },
    /**
     * Used for ease of lookup during grid render
     * @returns Object of widgets keyed by widgetId
     */
    widgetsById(): Record<string, DashboardWidget> {
      return this.arrayToObject(this.widgets, 'id')
    },
    showEmptyMessage(): boolean {
      return this.grid?.layout?.length === 0 && this.widgets?.length === 0
    },
    verticalCompact(): boolean {
      const isEnabled = this.featureFlagsStore.featureFlagBySlug(
        FeatureFlag.DashboardVerticalCompactEnabled
      )
      // Dashboard launched with vertical-compact set to true
      // This preserves that legacy behavior
      // if the feature flag's value is not explicitly false
      return isEnabled !== false
    },
    restoreOnDrag(): boolean {
      const isEnabled = this.featureFlagsStore.featureFlagBySlug(
        FeatureFlag.DashboardRestoreOnDragEnabled
      )
      return !!isEnabled
    },
    allDevices(): Record<string, UnpackedDevice> {
      return this.$store.state.devices.allDevices
    },
    devicesLength(): number {
      return Object.entries(this.allDevices).length
    },
    devicesCounts() {
      let totalDevices = 0
      let activeDevices = 0
      let inactiveDevices = 0
      const deviceIds: string[] = []

      for (const [devId, device] of Object.entries(this.allDevices)) {
        const isShared = device.isShared
        const isOwned = this.accountId === device.accountId
        if (isShared || isOwned) {
          totalDevices++
          deviceIds.push(devId)
          const lastContact =
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_CONTACT_BY_DEVICE_ID}`
            ](devId)
          if (activeLastDay(lastContact)) {
            activeDevices++
          } else {
            inactiveDevices++
          }
        }
      }
      return { totalDevices, activeDevices, inactiveDevices, deviceIds }
    }
  },
  watch: {
    /**
     * Changes layout when viewport changes
     */
    viewport(newBreakpoint: string, oldBreakpoint: string) {
      if (newBreakpoint) {
        if (oldBreakpoint && this.grid.widgetMoved) {
          this.resetWidgetOrder(oldBreakpoint)
          this.grid.widgetMoved = false
        }
        this.grid.layout = this.grid.layouts[newBreakpoint as Viewport]
          ? this.grid.layouts[newBreakpoint as Viewport]
          : []
        // Create a layout for widgets without one.
        this.patchLayouts()
      }
    }
  },
  methods: {
    resolveWidgetComponent(widgetComponent: string): Component | null {
      try {
        const component = WidgetComponents[widgetComponent as WidgetComponentKey]
        if (!component) {
          throw new Error(`Unknown widget component key: ${widgetComponent}`)
        }
        return component
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Dashboard] Widget component not found', error)
        return null
      }
    },
    async getWidgets(): Promise<void> {
      const resp = await this.$api.getWidgets()
      if (resp.ok) {
        const widgets = await resp.json()
        this.widgets = widgets.map((widget) => this.setWidgetSizes(widget))
      } else {
        this.notifications.addMessage({
          text: this.$t('dashboard.messages.warningLoading'),
          type: MessageType.WARNING,
          timeout: SNACKBAR_STATIC_DURATION_MS
        })
      }
    },
    async getLayouts(): Promise<void> {
      let layouts: Partial<Layouts> = {}
      const resp = await this.$api.getLayout()
      if (resp.ok) {
        const body = await resp.json()
        if (body?.layouts) {
          layouts = body.layouts
        }
      } else if (resp.status !== HTTPStatus.NotFound) {
        // The dashboard layout collection and document do not exist
        // until a user visits the Dashboard for the first time.
        // The API response will include a status code 404,
        // and we don’t want to trigger a snackbar warning in this case.
        this.notifications.addMessage({
          text: this.$t('dashboard.messages.warningLoading'),
          type: MessageType.WARNING,
          timeout: SNACKBAR_STATIC_DURATION_MS
        })
      }

      // create empty layouts for all breakpoints if none are returned
      const isEmpty =
        Object.values(layouts).every((x) => !x || x.length === 0) ||
        Object.keys(layouts).length === 0
      if (isEmpty) {
        for (const breakpoint of Object.keys(this.grid.breakpoints)) {
          layouts[breakpoint as Viewport] = []
        }
        this.grid.layouts = layouts as GridLayouts
        // add any pre-existing widgets to the layouts
        this.addWidgetsToLayout(this.widgets)
      } else {
        this.grid.layouts = layouts as GridLayouts
        this.updateLayoutItemSizes()
      }
    },
    patchLayouts(): void {
      this.widgets.forEach((widget) => {
        const found = this.grid.layout.find((item) => item.i === widget.id)
        if (!found) this.addWidgetsToLayout([widget])
      })
    },
    async saveLayouts(): Promise<void> {
      if (!this.grid.loading) {
        if (this.accountId) {
          const normalizedLayouts = {
            xxs: this.grid.layouts.xxs ?? [],
            xs: this.grid.layouts.xs ?? [],
            sm: this.grid.layouts.sm ?? [],
            md: this.grid.layouts.md ?? [],
            lg: this.grid.layouts.lg ?? []
          }
          const payload = {
            accountId: this.accountId,
            layouts: normalizedLayouts
          }
          await this.$api.postLayout(payload)
        }
      }
    },
    /**
     * Set layouts to locked if they contain a userModified item
     */
    setLockedLayouts(): void {
      const locked = {} as Record<Viewport, boolean>
      for (const viewport in this.grid.layouts) {
        const moved = this.grid.layouts[viewport as Viewport].some(
          (item) => item.userModified === true
        )
        locked[viewport as Viewport] = moved
      }
      this.grid.layoutsLocked = locked
    },
    layoutUpdated(): void {
      this.saveLayouts()
      this.setLockedLayouts()
    },
    openAddWidgets(): void {
      this.dialogs.addWidgets = true
    },
    closeAddWidgets(): void {
      this.dialogs.addWidgets = false
    },
    async addNewWidget(event: {
      deviceIds?: string[]
      widgetComponent?: WidgetComponentKey
    }): Promise<void> {
      if (event && event.deviceIds && event.widgetComponent) {
        const newWidget: PostWidgetRequest = {
          deviceIds: event.deviceIds,
          widgetComponent: event.widgetComponent
        }
        const resp = await this.$api.postWidget(newWidget)
        if (resp.ok) {
          this.refreshDashboard()
        } else {
          this.notifications.addMessage({
            text: this.$t('dashboard.messages.errorAddWidget'),
            type: MessageType.ERROR,
            timeout: SNACKBAR_STATIC_DURATION_MS
          })
        }
      }
    },
    async removeWidget(): Promise<void> {
      const index = this.widgets.findIndex((item) => item.id === this.widgetToRemove)
      if (typeof index === 'number') {
        const widget = this.widgets[index]
        const resp = await this.$api.deleteWidget(widget.id)
        if (resp.ok || resp.status === HTTPStatus.NotFound) {
          this.removeWidgetsFromLayout([widget])
          this.widgets.splice(index, 1)
        } else {
          this.notifications.addMessage({
            text: this.$t('dashboard.messages.errorRemoveWidget'),
            type: MessageType.ERROR,
            timeout: SNACKBAR_STATIC_DURATION_MS
          })
        }
        this.clearWidgetToRemove()
      }
    },
    async refreshDashboard(): Promise<void> {
      await this.getWidgets()
      this.patchLayouts()
    },
    /**
     * Add passed widgets to all layouts
     * @param {Array} widgets to add
     * @param {Boolean} skipLockedLayouts (optional) flag for whether locked layouts should be skipped
     */
    addWidgetsToLayout(widgets: DashboardWidget[], skipLockedLayouts = false): void {
      for (const viewport in this.grid.layouts) {
        if (skipLockedLayouts && this.grid.layoutsLocked[viewport as Viewport]) {
          continue
        }
        widgets.forEach((widget) => {
          this.addItem(widget, viewport as Viewport)
        })
      }
    },
    /**
     * Removes widgets from all grid layouts
     * @param {Array} widgets to remove
     * @param {Boolean} skipLockedLayouts whether to skip locked layouts
     */
    removeWidgetsFromLayout(widgets: DashboardWidget[], skipLockedLayouts = false): void {
      for (const viewport in this.grid.layouts) {
        if (skipLockedLayouts && this.grid.layoutsLocked[viewport as Viewport]) {
          continue
        }
        widgets.forEach((widget) => {
          this.removeItem(widget, viewport as Viewport)
        })
      }
    },
    /**
     * Updates layout items dims to match contained widget
     * Can be called for a single widget
     * @param {Object} widget for which to update items or null to update all items
     */
    updateLayoutItemSizes(widget: DashboardWidget | null = null): void {
      for (const viewport in this.grid.layouts) {
        const layout = this.grid.layouts[viewport as Viewport]
        if (widget) {
          const layoutItem = layout.find((item) => item.i === widget.id)
          if (layoutItem) {
            layoutItem.isResizeable = widget.isResizable
            layoutItem.w = widget.w
            layoutItem.h = widget.h
            layoutItem.maxW = widget.maxW
            layoutItem.maxH = widget.maxH
          }
        } else {
          layout.forEach((item) => {
            const itemWidget = this.widgetsById[item.i]
            if (itemWidget) {
              item.isResizeable = itemWidget.isResizable
              item.w = itemWidget.w
              item.h = itemWidget.h
              item.maxW = itemWidget.maxW
              item.maxH = itemWidget.maxH
            }
          })
        }
      }
    },
    /**
     * Determine size support for widget
     * set w/h and resize min/max accordingly
     * @param {Object} item widget to update
     */
    setWidgetSizes(item: Widget | DashboardWidget): DashboardWidget {
      const widget = { ...item } as DashboardWidget
      const widgetComponentKey = item.widgetComponent as WidgetComponentKey
      const sizes = this.widgetSizes[widgetComponentKey]
      if (!sizes) {
        throw new Error(`Unknown widget component key in setWidgetSizes: ${item.widgetComponent}`)
      }
      const minW = Math.min(...sizes.map((size) => this.grid.sizes[size].w))
      const minH = Math.min(...sizes.map((size) => this.grid.sizes[size].h))
      const maxW = Math.max(...sizes.map((size) => this.grid.sizes[size].w))
      const maxH = Math.max(...sizes.map((size) => this.grid.sizes[size].h))

      widget.isResizable = !(minW === maxW && minH === maxH)
      const properties = item.properties ?? {}
      widget.w = properties.w >= minW && properties.w <= maxW ? properties.w : minW
      widget.h = properties.h >= minH && properties.h <= maxH ? properties.h : minH
      widget.maxW = maxW
      widget.maxH = maxH

      return widget
    },
    /**
     * Adds a new item to all the grid layouts
     * Item is placed at the end of the grid
     * @param {Object} item widget to add
     * @param {String} viewport to update
     */
    addItem(item: DashboardWidget, viewport: Viewport): void {
      // set size support for widget
      item = this.setWidgetSizes(item)
      // get the next open cell
      const { x, y } = this.getNextOpenCell(viewport, item)
      // get the layout for the given layout and add item
      const layout = this.grid.layouts[viewport as Viewport]
      layout.push({
        x,
        y,
        w: item.w as number,
        h: item.h as number,
        maxW: item.maxW,
        maxH: item.maxH,
        isResizeable: item.isResizable,
        i: item.id
      })
    },
    /**
     * Removes an item from all grid layouts
     * @param {Object} item widget to remove
     * @param {String} viewport to update
     */
    removeItem(item: DashboardWidget, viewport: Viewport): void {
      const layout = this.grid.layouts[viewport]
      const index = layout.map((el) => el.i).indexOf(item.id)
      layout.splice(index, 1)
    },
    /**
     * Find a widget in the widgets array
     * @param {String} id of widget
     * @returns Object of widget
     */
    getWidget(id: string): DashboardWidget | undefined {
      return this.widgets.find((widget) => widget.id === id)
    },
    handleRemoveWidget(removeId: string, options?: { force?: boolean }): void {
      this.widgetToRemove = removeId
      if (options?.force) {
        this.removeWidget()
      } else {
        this.dialogs.removeWarning = true
      }
    },
    clearWidgetToRemove(): void {
      this.widgetToRemove = null
      this.dialogs.removeWarning = false
    },
    /**
     * Set the flag that a widget moved
     * Add the userModified property to the layout item (used to lock layout)
     */
    setWidgetMoved(i: string): void {
      this.grid.widgetMoved = true
      const item = this.grid.layouts[this.viewport as Viewport].find((item) => item.i === i)
      if (item) item.userModified = true
    },
    /**
     * Get the layout order of widgets for the given grid map
     * @param {Array} map to evaluate
     * @return {Array} of item IDs in display order
     */
    getLayoutOrder(map: string[][]): string[] {
      const itemSet = new Set<string>()
      for (const row of map) {
        for (const cell of row) {
          if (cell !== '') {
            itemSet.add(cell)
          }
        }
      }
      return [...itemSet]
    },
    /**
     * Reset the widget order on non-visible layouts
     * to match the order of the visible layout
     * Locked layouts will not be updated
     * @param {String} viewport to use as sort order
     */
    resetWidgetOrder(viewport: string): void {
      // get the rendered order of items in the visible layout
      const map = this.gridMaps[viewport].map
      const order = this.getLayoutOrder(map)
      // sort widgets by layout order
      const sorted = [...this.widgets].sort((a, b) =>
        order.indexOf(a.id) > order.indexOf(b.id) ? 1 : -1
      )
      // remove widgets from layouts, skipping locked layouts
      this.removeWidgetsFromLayout(this.widgets, true)
      // add sorted widgets back to layouts, skipping locked layouts
      this.addWidgetsToLayout(sorted, true)
    },
    /**
     * Set the grid and pixel dimensions on the widget
     * when the grid item changes size
     */
    setWidgetDims(i: string, newH: number, newW: number, newHPx: number, newWPx: number): void {
      const widget = this.getWidget(i)
      if (!widget) return
      let updateItem = false
      if (newW !== widget.w) {
        widget.w = parseInt(String(newW))
        updateItem = true
      }
      if (newH !== widget.h) {
        widget.h = parseInt(String(newH))
        updateItem = true
      }
      if (updateItem) {
        this.updateLayoutItemSizes(widget)
        this.$api.patchWidget({ properties: { w: newW, h: newH } }, widget.id)
      }
      widget.width = parseInt(String(newWPx))
      widget.height = parseInt(String(newHPx))
    },
    toggleFullscreen(widgetId: string, isFullscreen: boolean): void {
      for (const viewport in this.grid.layouts) {
        const layout = this.grid.layouts[viewport as Viewport]
        const item = layout.find((el) => el.i === widgetId)
        if (item) item.isFullscreen = isFullscreen
      }
    },
    updateWidgetDevices(widgetId: string, deviceIds: string[]): void {
      const widget = this.widgetsById[widgetId]
      if (widget) widget.deviceIds = deviceIds
    },
    /**
     * Collect all open cells for the given grid map
     * @param   {Array} map to loop over
     * @return  {Array} of open cells
     */
    getOpenCells(map: string[][]): { x: number; y: number }[] {
      const cells: { x: number; y: number }[] = []
      for (let y = 0; y < map.length; y++) {
        for (let x = 0; x < map[y].length; x++) {
          if (map[y][x] === '') {
            cells.push({ x, y })
          }
        }
      }
      return cells
    },
    /**
     * Find the next open cell in the grid for a given viewport
     * large enough to place the a new widget
     * @return  {Object} with cell coordinates
     */
    getNextOpenCell(viewport: string, item: DashboardWidget): { x: number; y: number } {
      const grid = this.gridMaps[viewport]
      const { map, height } = grid
      const openCells = this.getOpenCells(map)
      // find first open cell with enough open adjacent cells
      // to fit the size of the widget being placed
      // return null if suitable space is not found
      const nextCell = openCells.find((cell) => {
        const isOpen: boolean[] = []
        for (let y = cell.y; y < cell.y + (item.h as number); y++) {
          for (let x = cell.x; x < cell.x + (item.w as number); x++) {
            // if map[y] is undefined, it is a new row to be added
            isOpen.push(map[y] === undefined || map[y][x] === '')
          }
        }
        return !isOpen.includes(false) ? cell : null
      })
      // define new cell coordinates
      return nextCell ?? { x: 0, y: height }
    },
    /**
     * Build 2D grid map for given layout
     * @param  {Array} layout array of items to map
     * @param  {Number} width of grid
     * @param  {Number} height of grid
     * @return {Array} 2D array of cells with widget locations marked
     */
    buildGridMap(layout: LayoutItem[], width: number, height: number): string[][] {
      // build empty grid map
      const map: string[][] = []
      for (let y = 0; y < height; y++) {
        map[y] = []
        for (let x = 0; x < width; x++) {
          map[y][x] = ''
        }
      }
      // mark occupied cells in grid map
      layout.forEach((item) => {
        for (let y = item.y; y < item.y + item.h; y++) {
          for (let x = item.x; x < item.x + item.w; x++) {
            // items that are normally wider than one cell
            // are forced to one cell width at XXS viewport
            // only update array elements that exist in grid map
            if (map[y]?.[x] === undefined) continue
            map[y][x] = item.i
          }
        }
      })
      return map
    },
    arrayToObject<T extends Record<string, unknown>>(array: T[], key: string): Record<string, T> {
      const object: Record<string, T> = {}
      array.forEach((item) => {
        object[item[key] as string] = item
      })
      return object
    },
    observe(el: Element): void {
      const setGridDims = throttle((entries: ResizeObserverEntry[]) => {
        const cr = entries[0].contentRect
        this.grid.dims.w = cr.width
        this.grid.dims.h = cr.height
      }, 200)
      this.observer = new ResizeObserver(setGridDims)
      if (el instanceof Element) {
        this.observer.observe(el)
      }
    },
    getWidgetsIds(): string[] {
      if (this.widgets && this.widgets.length) {
        return this.widgets.map((widget) => widget.id)
      }
      return []
    },
    filterLayoutsWithoutWidgets(): GridLayouts | null {
      let layouts: GridLayouts | null = null
      if (this.grid && this.grid.layouts) {
        layouts = { ...this.grid.layouts }
        const widgetIds = this.getWidgetsIds()
        for (const [breakpoint, layout] of Object.entries(layouts)) {
          layouts[breakpoint as Viewport] = layout.filter(
            (item) => widgetIds.indexOf(item.i) !== -1
          )
        }
      }
      return layouts
    },
    async sanitizeLayouts(): Promise<void> {
      const filteredLayoutsWithoutWidgets = this.filterLayoutsWithoutWidgets()
      if (filteredLayoutsWithoutWidgets && this.grid && this.grid.layouts) {
        if (JSON.stringify(filteredLayoutsWithoutWidgets) !== JSON.stringify(this.grid.layouts)) {
          this.grid.layouts = filteredLayoutsWithoutWidgets
          await this.saveLayouts()
        }
      }
    },
    stopLoading(): void {
      this.isLoaded = true
      this.grid.loading = false
    }
  },
  async mounted() {
    const gridEl = (this.$refs.dashboardGrid as InstanceType<typeof GridLayout>).$el
    this.observe(gridEl)
    this.accountId = getUserAccountId(this.$auth)
    await this.getWidgets()
    await this.getLayouts()
    await this.sanitizeLayouts()
    this.stopLoading()
  },
  unmounted() {
    if (this.observer) this.observer.disconnect()
  }
})
</script>

<style lang="scss">
.vue-draggable-handle {
  touch-action: none;
  cursor: grab;

  .vgl-item--dragging & {
    cursor: grabbing;
  }

  .widget-fullscreen & {
    cursor: default;
  }
}
</style>
<style lang="scss" scoped>
@use '@/assets/global-variables' as *;

:deep(.vgl-item) {
  --vgl-placeholder-bg: var(--tsi-color--main-primary);
  --vgl-resizer-size: 20px;

  touch-action: pan-x pan-y !important;
  width: 400px;

  &.vgl-item--placeholder {
    border-radius: 4px;

    .vgl-item__resizer {
      display: none;
    }
  }

  &:not(.vgl-item--placeholder) {
    background: transparent;
  }

  .debug {
    position: absolute;
    top: 100%;
    left: 0;
    font-size: 75%;
    line-height: 1;
    margin: 0.25em 0;
  }

  .widget {
    width: 100%;
    height: 100%;
    user-select: none;
  }

  .vgl-item__resizer {
    touch-action: none;
    border-radius: 4px;
    background-color: #fff;
    &::before {
      content: '\F045D';
      top: auto;
      left: auto;
      display: inline-block;
      font-family: 'Material Design Icons', sans-serif;
      font-size: 16px;
      line-height: 1;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      border: 0 !important;
    }
  }
}

.dashboard {
  min-height: calc(100dvh - var(--v-layout-top));
  border-radius: 0;
  box-shadow: none;
  background: $background-gradient;
  padding-top: 24px;
  padding-right: 24px;
  padding-bottom: calc(var(--row-height) + 24px);
  padding-left: 24px;

  .page-title {
    display: inline-block;
    padding-top: 0;
    padding-left: 0;
  }

  .flex-container {
    display: flex;
    flex-wrap: wrap;
    padding: 8px;
  }
}

.dashboard-grid {
  margin-top: -8px;
  margin-right: calc(var(--widget-margin) * -1);
  margin-bottom: 0;
  margin-left: calc(var(--widget-margin) * -1);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 300;
  padding-bottom: 0;
}

.v-btn.icon-btn {
  min-width: 48px;
  padding: 0;
}
.card-title-btn {
  margin-left: 8px;
  .append-plus {
    align-self: flex-start;
    margin-left: -0.25em;
  }
}

.devices-summary {
  font-size: 0.75rem;
  padding: 16px;
  @media (min-width: 960px) {
    padding: 0;
  }
  @media (min-width: 480px) {
    font-size: 0.875rem;
  }
  &__group {
    padding: 0.25em 1em 0;
    &:last-of-type {
      padding-right: 0;
    }
    > span {
      display: inline-block;
      vertical-align: baseline;
    }
  }
  a {
    color: inherit;
    text-decoration: none;
  }
  .devices-count {
    font-weight: bold;
    font-size: 1.5em;
    padding-right: 0.25em;
  }
  .active {
    color: #00e400;
  }
  .inactive {
    color: #9e9e9e;
  }
  .border-right {
    border-right: 1px solid #cccccc;
  }
}

.empty-message {
  font-size: 16px;
  height: 80px;
  line-height: 240px;
  opacity: 0.56;
}
.disabled-message {
  font-size: 1rem;
  padding: 1rem 0;
  opacity: 0.76;
}
</style>
